import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, ArrowRight, Loader2, History, ChevronDown, RotateCcw, Users, AlertTriangle
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { API } from "../../App";

// Reasons accepted by POST /units/{id}/write-off — must match backend
// literal, DO NOT localise the wire values (only the labels).
const WRITE_OFF_REASONS = [
  { value: "broken", label: "Broken" },
  { value: "lost", label: "Lost" },
  { value: "spilled", label: "Spilled" },
  { value: "other", label: "Other" },
];

// Best-effort classifier: is this unit a REUSABLE tool (i.e. no partial
// write-off, always full-unit)? The backend hangs `is_reusable` on the
// product, not the unit — some existing endpoints (/units listing) may
// or may not carry it through. Fall back on category heuristics that
// match the backend's default seeding ("Tools/Equipment", "Blades /
// Cutting tools", "Equipment") so the UI degrades gracefully even when
// the field is missing. Consumables get the amount input; reusables
// don't. Being wrong here only affects the UI — the backend still
// applies the correct semantics regardless.
const REUSABLE_CATEGORIES = new Set([
  "Tools/Equipment",
  "Tools",
  "Equipment",
]);
const _isReusableUnit = (u) => {
  if (u?.is_reusable === true) return true;
  if (u?.is_reusable === false) return false;
  return REUSABLE_CATEGORIES.has(u?.category);
};

const StockPullSection = () => {
  const [staticUnits, setStaticUnits] = useState([]);
  const [floatUnits, setFloatUnits] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentPulls, setRecentPulls] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showFloat, setShowFloat] = useState(true);

  // --- Write-off dialog state ---
  // Single dialog reused for any unit (static or float). Populated with
  // the unit object so we know whether to allow a partial `amount` (only
  // meaningful for consumables — reusable tools are always full-unit
  // write-offs, so we hide the amount input in that case).
  const [woUnit, setWoUnit] = useState(null); // unit object, null = closed
  const [woReason, setWoReason] = useState("broken");
  const [woAmount, setWoAmount] = useState("");
  const [woNotes, setWoNotes] = useState("");
  const [woSubmitting, setWoSubmitting] = useState(false);

  // --- Pull-intercept dialog state (Part 2) ---
  // When POST /units/{id}/pull returns 409 with { blocked: true,
  // reason: "already_holding", ... }, we open this dialog so the user
  // can pick one of the two auto-resolve paths that hit the backend's
  // ?resolve=return / ?resolve=lost_broken query.
  const [intercept, setIntercept] = useState(null); // { unitId, existingUnitId, productName, message }
  const [interceptStage, setInterceptStage] = useState("choose"); // "choose" | "lost_broken"
  const [interceptReason, setInterceptReason] = useState("lost");
  const [interceptNotes, setInterceptNotes] = useState("");
  const [interceptSubmitting, setInterceptSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [staticRes, floatRes, pullsRes] = await Promise.all([
        axios.get(`${API}/stock/units?location=static`),
        axios.get(`${API}/stock/my-float`),
        axios.get(`${API}/stock/pulls?limit=10`)
      ]);
      
      setStaticUnits(staticRes.data || []);
      setFloatUnits(floatRes.data?.units || []);
      setRecentPulls(pullsRes.data || []);
    } catch (error) {
      console.error("Failed to load stock data:", error);
      toast.error("Failed to load stock units");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePull = async () => {
    if (!selectedUnit) {
      toast.error("Please select a unit to pull");
      return;
    }

    const unit = staticUnits.find(u => u.unit_id === selectedUnit);
    if (!unit) {
      toast.error("Unit not found");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/stock/units/${selectedUnit}/pull`);

      toast.success(`Pulled ${unit.product_name} (${unit.remaining_display}) to shared float`);

      // Reset form and reload data
      setSelectedCategory("");
      setSelectedUnit("");
      loadData();
    } catch (error) {
      // Part 2 — reusable-tool interception. Backend returns 409 with a
      // payload we can turn into a two-button prompt. Detail can arrive
      // as a nested object OR as a JSON string depending on how FastAPI
      // serialises it in the current version — normalise both.
      const status = error.response?.status;
      let detail = error.response?.data?.detail;
      if (typeof detail === "string") {
        try { detail = JSON.parse(detail); } catch { /* not JSON */ }
      }
      if (status === 409 && detail && detail.blocked && detail.reason === "already_holding") {
        setIntercept({
          unitId: selectedUnit,
          existingUnitId: detail.existing_unit_id,
          productName: detail.product_name,
          message: detail.message,
        });
        setInterceptStage("choose");
        setInterceptReason("lost");
        setInterceptNotes("");
      } else {
        toast.error(error.response?.data?.detail || "Failed to pull stock");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resolve a 409 interception by re-hitting /pull with the appropriate
  // ?resolve query param. Backend handles the return/write-off of the
  // held unit atomically and then completes the new pull — one round
  // trip, no client-side orchestration required.
  const resolvePull = async (resolveKind, extraQuery = "") => {
    if (!intercept) return;
    setInterceptSubmitting(true);
    try {
      await axios.post(
        `${API}/stock/units/${intercept.unitId}/pull?resolve=${resolveKind}${extraQuery}`,
      );
      toast.success(
        resolveKind === "return"
          ? "Previous unit returned; new one is now in float."
          : "Previous unit written off; replacement pulled.",
      );
      setIntercept(null);
      setSelectedCategory("");
      setSelectedUnit("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resolve pull");
    } finally {
      setInterceptSubmitting(false);
    }
  };

  const handleReturn = async (unitId) => {
    try {
      await axios.post(`${API}/stock/units/${unitId}/return`);
      toast.success("Returned to static storage");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to return stock");
    }
  };

  // Open the write-off dialog for a specific unit. We stash the WHOLE
  // unit object so the dialog can decide whether to show the `amount`
  // input (consumables only) — reusable tools always write off in full.
  const openWriteOff = (unit) => {
    setWoUnit(unit);
    setWoReason("broken");
    setWoAmount("");
    setWoNotes("");
  };

  const closeWriteOff = () => {
    if (woSubmitting) return;
    setWoUnit(null);
  };

  const submitWriteOff = async () => {
    if (!woUnit) return;
    setWoSubmitting(true);
    try {
      const body = { reason: woReason };
      const isReusable = _isReusableUnit(woUnit);
      if (!isReusable && woAmount) {
        const n = Number(woAmount);
        if (!Number.isFinite(n) || n <= 0) {
          toast.error("Amount must be a positive number");
          setWoSubmitting(false);
          return;
        }
        body.amount = n;
      }
      if (woNotes.trim()) body.notes = woNotes.trim();
      await axios.post(`${API}/stock/units/${woUnit.unit_id}/write-off`, body);
      toast.success(`Write-off logged for ${woUnit.product_name}`);
      setWoUnit(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log write-off");
    } finally {
      setWoSubmitting(false);
    }
  };

  const selectedUnitData = staticUnits.find(u => u.unit_id === selectedUnit);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('en-IE', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Group units by category, then by product for display
  const groupedByCategory = staticUnits.reduce((acc, unit) => {
    const category = unit.category || "Other";
    if (!acc[category]) {
      acc[category] = {};
    }
    const productName = unit.product_name;
    if (!acc[category][productName]) {
      acc[category][productName] = [];
    }
    acc[category][productName].push(unit);
    return acc;
  }, {});

  // Category order for consistent display
  const categoryOrder = [
    "Films/Wraps",
    "Chemicals/Liquids", 
    "Blades/Cutting tools",
    "Tools/Equipment",
    "Consumables",
    "Other"
  ];

  // Get available categories (only those with stock)
  const availableCategories = Object.keys(groupedByCategory).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Get units for selected category (flattened)
  const unitsInCategory = selectedCategory 
    ? Object.entries(groupedByCategory[selectedCategory] || {}).flatMap(([productName, units]) => 
        units.map(u => ({ ...u, productName }))
      )
    : [];

  if (isLoading) {
    return (
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
          <Package className="w-5 h-5 text-saturn-orange" />
          Stock Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pull to Shared Float Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-saturn-orange" />
            <p className="text-sm text-slate-300 font-medium">Pull to Shared Float</p>
          </div>
          <p className="text-xs text-slate-500">
            Move stock from storage to the shared float. Any team member can use it.
          </p>

          {/* Step 1: Category Selection */}
          <div className="space-y-2">
            <Label className="text-slate-400 text-xs">Step 1: Select Category</Label>
            <Select 
              value={selectedCategory} 
              onValueChange={(val) => {
                setSelectedCategory(val);
                setSelectedUnit(""); // Reset unit when category changes
              }}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="stock-pull-category-select">
                <SelectValue placeholder="Choose category..." />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.length > 0 ? (
                  availableCategories.map((category) => {
                    const unitCount = Object.values(groupedByCategory[category] || {}).flat().length;
                    return (
                      <SelectItem key={category} value={category}>
                        <span className="flex items-center justify-between w-full gap-4">
                          <span>{category}</span>
                          <span className="text-xs text-slate-500">({unitCount} units)</span>
                        </span>
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="_empty" disabled>No stock in storage</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2: Unit Selection (shown after category selected) */}
          {selectedCategory && (
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">Step 2: Select Unit</Label>
              <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="stock-pull-unit-select">
                  <SelectValue placeholder="Choose unit to pull..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(groupedByCategory[selectedCategory] || {}).map(([productName, units]) => (
                    <div key={productName}>
                      <div className="px-2 py-1.5 text-xs text-saturn-orange font-medium bg-slate-800/50 border-b border-slate-700">
                        {productName}
                      </div>
                      {units.map(unit => (
                        <SelectItem key={unit.unit_id} value={unit.unit_id}>
                          <span className="flex items-center gap-2">
                            {unit.status === "full" ? (
                              <span className="w-2 h-2 rounded-full bg-green-500" />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-yellow-500" />
                            )}
                            <span className="font-medium">{unit.remaining_display}</span>
                            {unit.status !== "full" && (
                              <span className="text-xs text-slate-500">
                                ({unit.percent_remaining}% left)
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selected Unit Info */}
          {selectedUnitData && (
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{selectedUnitData.product_name}</p>
                  <p className="text-sm text-slate-400">
                    {selectedUnitData.status === "full" ? "Full" : "Partial"} • {selectedUnitData.remaining_display}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-saturn-orange" />
                <div className="text-right">
                  <p className="text-saturn-orange font-medium flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Shared Float
                  </p>
                  <p className="text-sm text-slate-400">Available to all</p>
                </div>
              </div>
              {/* Escape hatch on a static unit: if it's actually broken /
                  lost / spilled in storage, log it here instead of pulling
                  it out to consume via /use. Prevents the "consume via
                  fake booking to zero it" anti-pattern. */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openWriteOff(selectedUnitData)}
                className="w-full text-amber-400 hover:text-amber-300 hover:bg-slate-800"
                data-testid={`static-writeoff-${selectedUnitData.unit_id}`}
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Log write-off for this unit
              </Button>
            </div>
          )}

          {/* Pull Button */}
          <Button
            onClick={handlePull}
            disabled={isSubmitting || !selectedUnit}
            className="w-full bg-saturn-orange hover:bg-saturn-orange/80"
            data-testid="stock-pull-btn"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ArrowRight className="w-4 h-4 mr-2" />
                Pull to Shared Float
              </>
            )}
          </Button>
        </div>

        {/* Shared Float Contents */}
        <Collapsible open={showFloat} onOpenChange={setShowFloat}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="shared-float-toggle"
            >
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-saturn-orange" />
                Shared Float ({floatUnits.length} items)
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFloat ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2 max-h-[250px] overflow-y-auto">
              {floatUnits.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No items in shared float</p>
              ) : (
                floatUnits.map((unit) => (
                  <div
                    key={unit.unit_id}
                    className="flex items-center justify-between bg-slate-800/30 rounded px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm truncate block">{unit.product_name}</span>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="text-saturn-orange">{unit.remaining_display}</span>
                        <span>•</span>
                        <span>Pulled by {unit.pulled_by || "Unknown"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openWriteOff(unit)}
                        className="text-amber-400 hover:text-amber-300 hover:bg-slate-700"
                        title="Log write-off (broken / lost / spilled)"
                        data-testid={`float-writeoff-${unit.unit_id}`}
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleReturn(unit.unit_id)}
                        className="text-slate-400 hover:text-white hover:bg-slate-700"
                        data-testid={`return-${unit.unit_id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Return
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Recent Pulls History */}
        <Collapsible open={showHistory} onOpenChange={setShowHistory}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="stock-pull-history-toggle"
            >
              <span className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Pulls
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
              {recentPulls.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No recent pulls</p>
              ) : (
                recentPulls.map((pull, idx) => (
                  <div
                    key={pull.pull_id || idx}
                    className="flex items-center justify-between bg-slate-800/30 rounded px-3 py-2 text-sm"
                  >
                    <div>
                      <span className="text-white">{pull.product_name}</span>
                    </div>
                    <div className="text-slate-500 text-xs">
                      {pull.pulled_by_name} • {formatDate(pull.pulled_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>

      {/* --- Write-off dialog ------------------------------------------- */}
      <Dialog open={!!woUnit} onOpenChange={(v) => { if (!v) closeWriteOff(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="writeoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Log write-off
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {woUnit && (
                <>
                  <span className="text-slate-200 font-semibold">{woUnit.product_name}</span>
                  {" · "}
                  {woUnit.remaining_display || `${woUnit.remaining_quantity ?? ""} ${woUnit.base_unit ?? ""}`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {woUnit && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Reason</Label>
                <Select value={woReason} onValueChange={setWoReason}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="writeoff-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WRITE_OFF_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Partial-amount input is meaningful ONLY for consumables.
                  For reusable tools it's always a full-unit write-off,
                  so we hide the input entirely rather than showing a
                  disabled field that invites confusion. */}
              {!_isReusableUnit(woUnit) && (
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">
                    Amount (optional — leave blank for full unit)
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={woAmount}
                    onChange={(e) => setWoAmount(e.target.value)}
                    placeholder={`e.g. 50 ${woUnit.base_unit || ""}`}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="writeoff-amount-input"
                  />
                  <p className="text-[11px] text-slate-500">
                    For partial waste (e.g. spilled 50ml of 500ml bottle). Empty = whole unit.
                  </p>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Notes (optional)</Label>
                <Input
                  type="text"
                  value={woNotes}
                  onChange={(e) => setWoNotes(e.target.value)}
                  placeholder="e.g. handle snapped, knocked over"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="writeoff-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeWriteOff}
              disabled={woSubmitting}
              className="border-slate-700 text-slate-300"
              data-testid="writeoff-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitWriteOff}
              disabled={woSubmitting}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="writeoff-submit-btn"
            >
              {woSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log write-off"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Pull-intercept dialog (409 already_holding, Part 2) ---------- */}
      <Dialog open={!!intercept} onOpenChange={(v) => { if (!v && !interceptSubmitting) setIntercept(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="pull-intercept-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              You already have one out
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              {intercept?.message}
            </DialogDescription>
          </DialogHeader>

          {interceptStage === "choose" && (
            <div className="space-y-2">
              <Button
                onClick={() => resolvePull("return")}
                disabled={interceptSubmitting}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white justify-start"
                data-testid="pull-intercept-return-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                I still have it — return that one
              </Button>
              <Button
                onClick={() => setInterceptStage("lost_broken")}
                disabled={interceptSubmitting}
                variant="outline"
                className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 justify-start"
                data-testid="pull-intercept-lostbroken-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                It&apos;s lost / broken
              </Button>
            </div>
          )}

          {interceptStage === "lost_broken" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Reason</Label>
                <Select value={interceptReason} onValueChange={setInterceptReason}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="pull-intercept-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="broken">Broken</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Notes (optional)</Label>
                <Input
                  type="text"
                  value={interceptNotes}
                  onChange={(e) => setInterceptNotes(e.target.value)}
                  placeholder="e.g. left at customer site"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="pull-intercept-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {interceptStage === "lost_broken" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setInterceptStage("choose")}
                  disabled={interceptSubmitting}
                  className="border-slate-700 text-slate-300"
                  data-testid="pull-intercept-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => resolvePull(
                    "lost_broken",
                    `&reason=${interceptReason}` + (interceptNotes.trim() ? `&notes=${encodeURIComponent(interceptNotes.trim())}` : ""),
                  )}
                  disabled={interceptSubmitting}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  data-testid="pull-intercept-confirm-btn"
                >
                  {interceptSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Write off & pull replacement"}
                </Button>
              </>
            )}
            {interceptStage === "choose" && (
              <Button
                variant="outline"
                onClick={() => setIntercept(null)}
                disabled={interceptSubmitting}
                className="border-slate-700 text-slate-300"
                data-testid="pull-intercept-cancel-btn"
              >
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StockPullSection;
